import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import uuid from 'uuid/v1';
import { useDropzone } from 'react-dropzone';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@mui/styles';
import {
	Button,
	IconButton,
	Link,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
	Tooltip,
	colors,
	Grid
} from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import MoreIcon from '@mui/icons-material/MoreVert';

import bytesToSize from 'utils/bytesToSize';

const useStyles = makeStyles((theme) => ({
	root: {},
	dropZone: {
		border: `1px dashed ${theme.palette.divider}`,
		padding: theme.spacing(6),
		outline: 'none',
		display: 'flex',
		justifyContent: 'center',
		flexWrap: 'wrap',
		alignItems: 'center',
		'&:hover': {
			backgroundColor: colors.grey[50],
			opacity: 0.5,
			cursor: 'pointer',
		},
	},
	dropZonePaste: {
		border: `1px dashed ${theme.palette.divider}`,
		padding: theme.spacing(6),
		outline: 'none',
		display: 'flex',
		justifyContent: 'center',
		flexWrap: 'wrap',
		alignItems: 'center',
	},
	dragActive: {
		backgroundColor: colors.grey[50],
		opacity: 0.5,
	},
	image: {
		width: 130,
	},
	info: {
		marginTop: theme.spacing(1),
	},
	list: {
		maxHeight: 320,
	},
	actions: {
		marginTop: theme.spacing(2),
		display: 'flex',
		justifyContent: 'flex-end',
		'& > * + *': {
			marginLeft: theme.spacing(2),
		},
	},
}));

const FilesDropzone = (props) => {
	const { multiple, acceptedTypes, upload, maxSize, className, title, ...rest } = props;

	const classes = useStyles();

	const [files, setFiles] = useState([]);

	const handleDrop = useCallback((acceptedFiles) => {
		if (multiple) {
			setFiles((f) => [...f].concat(acceptedFiles));
		} else {
			setFiles([...acceptedFiles]);
		}
	}, []);

	const [image, setImage] = useState(null);

	const handleRemoveAll = () => {
		setFiles([]);
		setImage(null);
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		multiple,
		accept: acceptedTypes,
		maxSize,
		onDrop: handleDrop,
	});

	const _upload = () => {
		if (upload) {
			upload(files, image);
		}
	};

	const handleOnPaste = (event) => {
		const { items } = event.clipboardData || event.originalEvent.clipboardData;

		let blob = null;
		for (let i = 0; i < items.length; i++) {
			if (items[i].type.indexOf('image') === 0) {
				blob = items[i].getAsFile();
			}
		}

		if (blob !== null) {
			const reader = new FileReader();
			reader.onload = function (eventer) {
				setImage(eventer.target.result);
			};
			reader.readAsDataURL(blob);
		}
	};

	return (
		<div {...rest} className={clsx(classes.root, className)} onPaste={(event) => handleOnPaste(event, title)}>
			<Grid container spacing={2}>
				<Grid item md={6}>
					<div
						style={{ height: '35vh' }}
						className={clsx({
							[classes.dropZone]: true,
							[classes.dragActive]: isDragActive,
						})}
						{...getRootProps()}
					>
						<input {...getInputProps()} />
						<div>
							<img
								alt="Select file"
								className={classes.image}
								src="./images/undraw_add_file2_gvbb.svg"
							/>
						</div>
						<div>
							<Typography gutterBottom variant="h3">
								Select files
							</Typography>
							<Typography
								className={classes.info}
								color="textSecondary"
								variant="body1"
							>
								Drop files here or click
								{' '}
								<Link underline="always">browse</Link>
								{' '}
								thorough your
								machine
							</Typography>
						</div>
					</div>
				</Grid>
				<Grid item md={6}>
					<div>
						<div
							hidden={image !== null}
						>
							<div
								style={{ height: '35vh' }}
								className={clsx({
									[classes.dropZonePaste]: true,
								})}
							>
								<input {...getInputProps()} />
								<div onPaste={(event) => handleOnPaste(event, title)}>
									<img
										alt="Select file"
										className={classes.image}
										src="./images/undraw_no_data_qbuo.svg"
									/>
								</div>
								<div onPaste={(event) => handleOnPaste(event, title)}>
									<Typography gutterBottom variant="h3">
										Paste image here
									</Typography>
								</div>
							</div>
						</div>
						<img
							hidden={image === null}
							src={image}
							alt="pic"
							height="200px"
						/>
					</div>
				</Grid>
			</Grid>
			{
				(files.length > 0 || image) && (
					<>
						<PerfectScrollbar options={{ suppressScrollX: true }}>
							<List className={classes.list}>
								{files.map((file, i) => (
									<ListItem
										divider={i < files.length - 1}
										key={uuid()}
									>
										<ListItemIcon>
											<FileCopyIcon />
										</ListItemIcon>
										<ListItemText
											primary={file.name}
											primaryTypographyProps={{
												variant: 'h5',
											}}
											secondary={bytesToSize(file.size)}
										/>
									</ListItem>
								))}
							</List>
						</PerfectScrollbar>
						<div className={classes.actions}>
							<Button onClick={handleRemoveAll} size="small">
								Remove
							</Button>
							<Button
								onClick={_upload}
								color="secondary"
								size="small"
								variant="contained"
							>
								Upload
							</Button>
						</div>
					</>
				)
			}
		</div>
	);
};

FilesDropzone.propTypes = {
	className: PropTypes.string,
	multiple: PropTypes.bool,
	acceptedTypes: PropTypes.string,
	maxSize: PropTypes.number,
	upload: PropTypes.func,
	title: PropTypes.string
};

export default FilesDropzone;
